/**
 * NavigationButton Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 471-148
 *
 * A navigation button component with icon and text support.
 * Supports light/dark modes and interactive states.
 */

import React, { useState } from 'react';
import { GestureResponderEvent, Pressable, Text, View, StyleSheet } from 'react-native';
import { Icon, type IconName } from '../icons';
import { colors } from '../tokens/colors';

export interface NavigationButtonProps {
  /**
   * Icon name to display
   */
  icon?: IconName;

  /**
   * Button text label
   */
  text?: string;

  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether to show the text
   * @default true
   */
  showText?: boolean;

  /**
   * Visual mode
   * @default 'light'
   */
  mode?: 'light' | 'dark';

  /**
   * Whether the button is in active state
   * @default false
   */
  active?: boolean;

  /**
   * Callback when button is pressed
   */
  onPress?: (e: GestureResponderEvent) => void;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon = 'Activity',
  text = 'Activity',
  showIcon = true,
  showText = true,
  mode = 'light',
  active = false,
  onPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determine current state for styling
  const currentState = active ? 'active' : isPressed ? 'active' : isHovered ? 'hover' : 'default';

  // Get styles based on mode and state using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (mode === 'dark') {
      switch (currentState) {
        case 'hover':
        case 'active':
          return colors.slate[100]; // #ECEEF1
        case 'default':
        default:
          return colors.slate[700]; // #262A33
      }
    } else { // light mode
      switch (currentState) {
        case 'active':
          return colors.neutral[700]; // #1A1A1A
        case 'hover':
          return colors.slate[600]; // #3A3F4D
        case 'default':
        default:
          return colors.slate[100]; // #ECEEF1 (light background for light mode)
      }
    }
  };

  const getTextColor = () => {
    if (mode === 'dark') {
      switch (currentState) {
        case 'hover':
        case 'active':
          return colors.brand.black; // #020202
        case 'default':
        default:
          return colors.brand.white; // #FCFCFC
      }
    } else { // light mode
      switch (currentState) {
        case 'hover':
        case 'active':
          return colors.brand.white; // #FCFCFC
        case 'default':
        default:
          return colors.brand.black; // #020202
      }
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.container,
        { backgroundColor },
      ]}
    >
      <View style={styles.content}>
        {showIcon && icon && (
          <Icon name={icon} size={24} color={textColor} />
        )}
        {showText && text && (
          <Text style={[styles.text, { color: textColor }]}>
            {text}
          </Text>
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 40,
    borderRadius: 99,
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  text: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
});

NavigationButton.displayName = 'NavigationButton';
